import { ButtonGroup, Tooltip, Button } from '@blueprintjs/core';
import styles from './Lender.module.css';
import { AvailableLender } from '../IAvailableLenders';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationData/ApplicationDataForm/ApplicationDataFormSchema';
import {
  useLoanQuotes,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import { RequestStatusGroup } from '@axo/shared/data-access/types/loan_request';
import { loan_quote, loan_request } from '@axo/shared/data-access/types';
import { useEffect, useRef } from 'react';

interface LenderSelectActionButtonsProps {
  applicationID: string;
  lenders: AvailableLender[];
}

export const LenderSelectActionButtons: React.FC<
  LenderSelectActionButtonsProps
> = ({ lenders, applicationID }) => {
  const { data: quotesData, isLoading: isQuotesDataLoading } =
    useLoanQuotes(applicationID);
  const { isInRequestStatusGroup } = useRequestStatusUtils();
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();

  const eligibleLenders: string[] = lenders
    .filter((lender) => !lender.state.disabled)
    .map((lender) => lender.lenderID);

  const rejectedLenders: string[] = lenders
    .filter((lender) =>
      quotesData?.some((quote) => {
        if (quote.LenderID !== lender.lenderID) return false;

        if (
          (!isInRequestStatusGroup(
            quote.LoanRequest.Status,
            RequestStatusGroup.Registered,
          ) &&
            quote.LoanRequest.Status !==
              loan_request.LoanRequestStatus
                .LoanRequestStatusApplicationCompletedNotRegisteredBankError) ||
          isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Active)
        )
          return false;

        return true;
      }),
    )
    .map((lender) => lender.lenderID);

  const { watch, setValue } = useFormContext<ApplicationDataFormValues>();
  const selectedLenders = watch('SelectedLenders');

  const defaultSelected = useRef<boolean>(false);
  useEffect(() => {
    if (defaultSelected.current === false && !isQuotesDataLoading) {
      selectedLenders.clear();
      rejectedLenders.forEach((lenderID) => selectedLenders.add(lenderID));
      setValue('SelectedLenders', selectedLenders);

      defaultSelected.current = true;
    }
  }, [isQuotesDataLoading, rejectedLenders, selectedLenders, setValue]);

  return (
    <ButtonGroup>
      <Tooltip
        disabled={eligibleLenders.length === 0}
        content={'No eligible lenders'}
      >
        <Button
          disabled={eligibleLenders.length === 0}
          minimal
          icon="align-justify"
          className={styles.buttonRightBorder}
          onClick={() => {
            eligibleLenders.forEach((lenderID) =>
              selectedLenders.add(lenderID),
            );
            setValue('SelectedLenders', selectedLenders);
          }}
        >
          Select all eligible
        </Button>
      </Tooltip>
      <Tooltip
        disabled={rejectedLenders.length === 0}
        content={'No lenders have declined'}
      >
        <Button
          disabled={rejectedLenders.length === 0}
          minimal
          icon="warning-sign"
          className={styles.buttonRightBorder}
          onClick={() => {
            rejectedLenders.forEach((lenderID) =>
              selectedLenders.add(lenderID),
            );
            setValue('SelectedLenders', selectedLenders);
          }}
        >
          Select declined
        </Button>
      </Tooltip>
      <Tooltip
        disabled={selectedLenders.size === 0}
        content={'No lenders have been selected'}
      >
        <Button
          minimal
          disabled={selectedLenders.size === 0}
          icon="clean"
          onClick={() => {
            selectedLenders.clear();
            setValue('SelectedLenders', selectedLenders);
          }}
        >
          Clear selection
        </Button>
      </Tooltip>
    </ButtonGroup>
  );
};
